import { ethers } from 'ethers';

export const ACCESS_SESSION_KEY = 'maiko_access_session_v1';

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '');

const isLocalBrowserHost = (hostname) => (
  hostname === 'localhost'
  || hostname === '127.0.0.1'
  || hostname === '::1'
);

export function getAccessApiBaseUrl({
  envUrl = process.env.REACT_APP_ACCESS_API_URL,
  nodeEnv = process.env.NODE_ENV,
  locationLike = typeof window !== 'undefined' ? window.location : null,
} = {}) {
  const configured = trimTrailingSlash(envUrl);
  if (configured) return configured;

  if (nodeEnv !== 'production' && (!locationLike?.hostname || isLocalBrowserHost(locationLike.hostname))) {
    return 'http://localhost:3003';
  }

  return '';
}

export function getStoredAccessSession() {
  try {
    const raw = localStorage.getItem(ACCESS_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session?.token && session?.identity ? session : null;
  } catch {
    return null;
  }
}

export function saveAccessSession(session) {
  localStorage.setItem(ACCESS_SESSION_KEY, JSON.stringify(session));
}

export function clearAccessSession() {
  localStorage.removeItem(ACCESS_SESSION_KEY);
}

function isMaikoRpcUrl(url) {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname === 'maiko.icu' || hostname.endsWith('.maiko.icu');
  } catch {
    return false;
  }
}

async function apiRequest(path, { token, body, ...options } = {}) {
  const sessionToken = token === undefined ? getStoredAccessSession()?.token : token;
  const response = await fetch(`${getAccessApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(sessionToken ? { authorization: `Bearer ${sessionToken}` } : {}),
      ...(options.headers || {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.reason || `權限服務回應 ${response.status}`);
    error.status = response.status;
    error.code = data.code;
    error.data = data;
    throw error;
  }
  return data;
}

export const getAccessStatus = () => apiRequest('/api/access/status');

export const setupAdmin = (username, password) => apiRequest('/api/access/admin/setup', {
  method: 'POST',
  token: '',
  body: { username, password },
});

export const loginAdmin = (username, password) => apiRequest('/api/access/admin/login', {
  method: 'POST',
  token: '',
  body: { username, password },
});

export const updateAdminCredentials = (username, password) => apiRequest('/api/access/admin/credentials', {
  method: 'POST',
  body: { username, password },
});

export const getAdminConfig = () => apiRequest('/api/access/admin/config');

export const saveAdminConfig = (config) => apiRequest('/api/access/admin/config', {
  method: 'PUT',
  body: config,
});

export const saveAdminBlockrazorAuthToken = (authToken, chainKey) => apiRequest('/api/access/admin/blockrazor', {
  method: 'PUT',
  body: { authToken, chainKey },
});

export const clearAdminBlockrazorAuthToken = () => apiRequest('/api/access/admin/blockrazor', {
  method: 'DELETE',
});

export const stopAdminBlockrazorGlobalStream = () => apiRequest('/api/access/admin/blockrazor/stop', {
  method: 'POST',
});

export const getRpcCatalog = () => apiRequest('/api/access/rpc-catalog');

export const isAccessApiNotFoundError = (error) => Number(error?.status) === 404;

export const logoutAccessSession = () => apiRequest('/api/access/logout', {
  method: 'POST',
}).catch(() => ({ ok: true }));

export async function connectInjectedWallet() {
  if (!window.ethereum) {
    throw new Error('找不到瀏覽器 Web3 錢包，請先安裝或啟用 MetaMask、Rabby、OKX Wallet 等擴充功能');
  }

  const provider = new ethers.BrowserProvider(window.ethereum);
  await provider.send('eth_requestAccounts', []);
  const signer = await provider.getSigner();
  const address = await signer.getAddress();
  const challenge = await apiRequest('/api/access/wallet/challenge', {
    method: 'POST',
    token: '',
    body: { address },
  });
  const signature = await signer.signMessage(challenge.message);
  return apiRequest('/api/access/wallet/verify', {
    method: 'POST',
    token: '',
    body: { challengeId: challenge.challengeId, address, signature },
  });
}

export async function checkRpcAccess(chainKey, url) {
  if (!isMaikoRpcUrl(url)) {
    return { ok: true, grantedBy: 'public' };
  }

  try {
    return await apiRequest('/api/access/check-rpc', {
      method: 'POST',
      body: { chainKey, url },
    });
  } catch (error) {
    if (error.status) {
      return {
        ok: false,
        reason: error.message,
        code: error.code || 'ACCESS_DENIED',
      };
    }
    return {
      ok: false,
      reason: 'Maiko RPC 權限服務目前無法連線',
      code: 'ACCESS_SERVICE_UNAVAILABLE',
    };
  }
}
